import { RoutingLink } from "#batteries-included-components/RoutingLink";
import { getCalculationInputTableCellDisplayValue } from "#batteries-included-components/Tabs/EstimationMethod/helpers";
import { useCustomAttributeFilters } from "#hooks/FilterPanel/useCustomAttributeFilters";
import { useListEquipment } from "#hooks/adapters/useEquipment";
import { useListFlows } from "#hooks/adapters/useFlows";
import { linkToEquipmentDetail } from "#routes/organization/equipment/[equipmentId]";
import { linkToFacilityDetail } from "#routes/organization/facilities/[facilityId]";
import { FlowDetailRoute } from "#routes/organization/flows/[flowId]/detail";
import { NetworkDetailRoute } from "#routes/organization/networks/[networkId]/detail";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import useLocalization from "#src/hooks/useLocalization";
import { useQueries, useQuery } from "@tanstack/react-query";
import { DataTable, HeaderType } from "@validereinc/common-components";
import {
  AssetGroupAdapter,
  AssetType,
  AssetTypeType,
  CalculationParameterType,
  CalculatorResultsDomain,
  EstimationMethodWithConfigurationAndRunType,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React from "react";

/** Add a `tableRowId` key to the configuration object for use with row selection */
export const withTableKey = (
  item: EstimationMethodWithConfigurationAndRunType
) => ({
  ...item,
  tableRowId: toTableKey(item),
});

export const toTableKey = (item: EstimationMethodWithConfigurationAndRunType) =>
  `${item.id}_${item.calculator_configuration?.year_month}`;

/** Convert a configuration object to an identifier for use in the API */
export const toApiKey = (
  item: EstimationMethodWithConfigurationAndRunType
) => ({
  estimation_method_id: item.id,
  year_month: item.calculator_configuration?.year_month,
});

export const getFacilityKey = (assetType: AssetTypeType) => {
  switch (assetType) {
    case AssetType.FLOW:
      return "flow.facility.id";
    case AssetType.EQUIPMENT:
      return "equipment.facility.id";
    default:
      return "facility.id";
  }
};

export const ASYNC_THRESHOLD = 25;

export const useGetMeasurementTypesFromCalculatorResultsBasedOnReportingGroup =
  (reportingGroupId: string, defaultMeasurementTypes: string[] = []) => {
    const params = {
      page: 1,
      pageSize: 1,
      filters: {
        reporting_group: reportingGroupId,
      },
    };

    const resultsQuery = useQuery({
      queryKey: ["calculatorResults", params],
      queryFn: () => CalculatorResultsDomain.getList(params),
      select: ({ data }) => data[0]?.measurement,
      enabled: !!reportingGroupId,
    });

    const measurementTypeList = Object.keys(resultsQuery.data ?? {});

    return {
      measurementTypeList: measurementTypeList.length
        ? measurementTypeList
        : defaultMeasurementTypes,
      isLoading: resultsQuery.isLoading,
    };
  };

export const useAssetSpecificCalculationHeaders = (
  assetType: AssetTypeType,
  items: EstimationMethodWithConfigurationAndRunType[],
  year_month: string[]
): {
  headers: Array<HeaderType<EstimationMethodWithConfigurationAndRunType>>;
  isLoading: boolean;
} => {
  const { localize } = useLocalization();

  const assetParams: Parameters<typeof AssetGroupAdapter.getList>[0] = {
    filters: {
      id: Array.from(
        new Set(
          items
            .filter(({ entity_type }) => entity_type === assetType)
            .map(({ entity_id }) => entity_id)
        )
      ),
    },
  };

  // @ts-expect-error TODO: fix filter typing
  const isAssetQueryReady = !!assetParams.filters?.id.length;
  const isEquipmentQueryEnabled =
    assetType === AssetType.EQUIPMENT && isAssetQueryReady;
  const isFlowQueryEnabled = assetType === AssetType.FLOW && isAssetQueryReady;
  const isNetworkQueryEnabled =
    assetType === AssetType.ASSET_GROUP && isAssetQueryReady;

  // @ts-expect-error TODO: fix filter typing
  const equipmentQuery = useListEquipment(assetParams, {
    select: ({ data }: any) => data,
    enabled: isEquipmentQueryEnabled,
  });

  const flowsQuery = useListFlows(assetParams, {
    select: ({ data }: any) => data,
    enabled: isFlowQueryEnabled,
  });

  const networkQueries = useQueries({
    queries: year_month.map((period) => ({
      queryKey: ["networks", period, assetType],
      queryFn: () =>
        AssetGroupAdapter.getList({
          meta: { period },
          filters: {
            id: Array.from(
              new Set(
                items
                  .filter(({ entity_type }) => entity_type === assetType)
                  .map(({ entity_id }) => entity_id)
              )
            ),
          },
        }),
      enabled: isNetworkQueryEnabled,
    })),
  });

  const networksQuery = isNetworkQueryEnabled
    ? networkQueries.map((query, idx) => ({
        period: year_month[idx],
        data: query.data?.data.flat(),
      }))
    : [];
  const isNetworkQueryLoading = isNetworkQueryEnabled
    ? networkQueries.some((query) => query.isLoading)
    : false;

  const {
    customAttributeHeaders,
    isLoading: isNetworkCustomAttributesLoading,
  } = useCustomAttributeFilters({
    assetType: AssetType.ASSET_GROUP,
  });

  const isLoading =
    (isEquipmentQueryEnabled && equipmentQuery.isLoading) ||
    (isFlowQueryEnabled && flowsQuery.isLoading) ||
    (isNetworkQueryEnabled && isNetworkQueryLoading) ||
    isNetworkCustomAttributesLoading;

  switch (assetType) {
    case AssetType.EQUIPMENT:
      return {
        headers: [
          {
            key: "equipment_details",
            label: "Equipment Details",
            headers: [
              {
                key: "equipment.facility.name",
                label: localize("Facility"),
                isSortable: true,
                renderComponent: ({ item }) => {
                  return (
                    <RoutingLink
                      to={linkToFacilityDetail((item as any).facility_id)}
                    >
                      {(item as any).facility_name}
                    </RoutingLink>
                  );
                },
              },
              {
                key: "entity_name",
                label: localize("Equipment"),
                isSortable: true,
                renderComponent: ({ item }) => (
                  <RoutingLink to={linkToEquipmentDetail(item.entity_id)}>
                    {(item as any).entity_name}
                  </RoutingLink>
                ),
              },
              {
                key: "equipment.type.name",
                label: localize("Equipment Type"),
                renderComponent: ({ item }) => {
                  const equipment = (equipmentQuery.data as any)?.find(
                    ({ id }: any) => id === item.entity_id
                  );
                  return equipment?.type?.name ?? "-";
                },
              },
            ],
          },
        ],
        isLoading,
      };
    case AssetType.FLOW:
      return {
        headers: [
          {
            key: "flow_details",
            label: "Flow Details",
            headers: [
              {
                key: "entity_name",
                label: localize("Flow"),
                isSortable: true,
                renderComponent: ({ item }) => (
                  <RoutingLink
                    to={FlowDetailRoute.toLink({
                      pathParams: { flowId: item.entity_id },
                    })}
                  >
                    {(item as any).entity_name}
                  </RoutingLink>
                ),
              },
              {
                key: "flow.facility.name",
                label: localize("Facility"),
                isSortable: true,
                renderComponent: ({ item }) => {
                  const flow = (flowsQuery.data as any)?.find(
                    ({ id }: any) => id === item.entity_id
                  );
                  return flow?.associated_facility ? (
                    <RoutingLink
                      to={linkToFacilityDetail(flow.associated_facility_id)}
                    >
                      {flow?.associated_facility?.name}
                    </RoutingLink>
                  ) : (
                    "-"
                  );
                },
              },
              {
                key: "flow.equipment.name",
                label: localize("Equipment"),
                isSortable: true,
                renderComponent: ({ item }) => {
                  const flow = (flowsQuery.data as any)?.find(
                    ({ id }: any) => id === item.entity_id
                  );
                  return flow?.associated_equipment ? (
                    <RoutingLink
                      to={linkToEquipmentDetail(flow.associated_equipment_id)}
                    >
                      {flow?.associated_equipment?.name}
                    </RoutingLink>
                  ) : (
                    "-"
                  );
                },
              },
              {
                key: "flow.type",
                label: localize("Flow Type"),
                isSortable: true,
                renderComponent: ({ item }) => {
                  const flow = (flowsQuery.data as any)?.find(
                    ({ id }: any) => id === item.entity_id
                  );
                  return toTitleCaseString(flow?.type, { intent: "ui" });
                },
              },
            ],
          },
        ],
        isLoading,
      };
    case AssetType.ASSET_GROUP:
      return {
        headers: [
          {
            key: "network_details",
            label: "Network Details",
            headers: [
              {
                key: "entity_name",
                label: "Network",
                isSortable: true,
                renderComponent: ({ item }) => (
                  <RoutingLink
                    to={NetworkDetailRoute.toLink({
                      pathParams: { networkId: item.entity_id },
                    })}
                  >
                    {(item as any).entity_name}
                  </RoutingLink>
                ),
              },
              ...customAttributeHeaders.map((customAttributeHeader) => ({
                ...customAttributeHeader,
                renderComponent: ({
                  item,
                }: {
                  item: EstimationMethodWithConfigurationAndRunType;
                }) => {
                  const value = networksQuery
                    ?.find(
                      ({ period }) =>
                        period === item.calculator_configuration?.year_month
                    )
                    ?.data?.find(({ id }) => id === item.entity_id)
                    ?.custom_attributes?.[
                    (customAttributeHeader as any).field_name
                  ];
                  if (value && customAttributeHeader.renderComponent) {
                    return customAttributeHeader.renderComponent({
                      value,
                    } as any);
                  }
                  return "-";
                },
              })),
            ],
          },
        ],
        isLoading: isNetworkCustomAttributesLoading,
      };
    default:
      return { headers: [], isLoading };
  }
};

export const useCalculationInputHeaders = (
  params?: CalculationParameterType[]
): {
  headers: Array<HeaderType<EstimationMethodWithConfigurationAndRunType>>;
  isLoading: boolean;
} => {
  const { getUnitName, isLoading } = useMeasurementTypes();

  if (!params?.length) {
    return { headers: [], isLoading };
  }

  return {
    isLoading,
    headers: [
      {
        key: "inputs",
        label: "Inputs",
        headers: params.map((input) => {
          const unit = getUnitName(input.measurement_unit);

          return {
            key: input.id,
            label: `${input.display_name}${unit ? ` [${unit}]` : ""}`,
            alignment: "right" as any,
            tooltip: input.description,
            isSortable: false,
            renderComponent: ({ item }) => {
              return getCalculationInputTableCellDisplayValue(
                {
                  type: input.type,
                  measurement_value:
                    item.calculator_configuration?.inputs?.[input.id]?.value ??
                    "",
                },
                "right"
              );
            },
          };
        }),
      },
    ],
  };
};

export const useCalculationOutputHeaders = ({
  measurementKeys,
  volumeAllowed,
  emissionsAllowed,
  networkAllowed,
  assetType,
}: {
  measurementKeys: string[];
  volumeAllowed: string[];
  networkAllowed: string[];
  emissionsAllowed: string[];
  assetType: AssetTypeType;
}): {
  headers: Array<HeaderType<EstimationMethodWithConfigurationAndRunType>>;
  isLoading: boolean;
} => {
  const { getTypeName, getPrecisionByType, getUnitByType, isLoading } =
    useMeasurementTypes();

  return {
    isLoading,
    headers: [
      {
        key: "results",
        label: "Results",
        headers: measurementKeys.map((key) => {
          const unit = getUnitByType(key);

          return {
            key: key,
            label: `${getTypeName(key)}${unit ? ` [${unit}]` : ""}`,
            alignment: "right" as any,
            isSelected:
              volumeAllowed.includes(key) ||
              emissionsAllowed.includes(key) ||
              (assetType === AssetType.ASSET_GROUP &&
                networkAllowed.includes(key))
                ? true
                : false,
            renderComponent: ({ item }) => {
              const outputValue =
                item.calculator_run?.run_output?.outputs?.find(
                  ({ measurement_type }) => measurement_type === key
                );
              const precision = getPrecisionByType(key);

              return (
                <DataTable.DataRow.NumberCell
                  value={outputValue?.measurement_value ?? ""}
                  formattingOpts={
                    precision
                      ? {
                          fractionDigits: precision,
                        }
                      : {}
                  }
                />
              );
            },
          };
        }),
      },
    ],
  };
};
